import React from "react";
import { formatCurrency } from "../../core/currency.core";
import MembershipPriceDisplay from "../common/membershipPriceDisplay";
import { CartItem } from "../../types/cart";
import { useTranslation } from "../../core/translation.core";
import { isCartItemPointRedeemed } from "../../core/pointRedemption.core";
import { PointRedemptionNoteForCartItem } from "./pointRedemptionDisplay.comp";

interface CartItemPriceDisplayProps {
  item: CartItem;
  itemTotal: number;
  quantity?: number;
}

export default function CartItemPriceDisplay({ item, itemTotal, quantity }: CartItemPriceDisplayProps) {
  const { t, msg } = useTranslation();
  const isAddOnItem = !!item.attachCartItemId;
  const isPointRedemptionItem = isCartItemPointRedeemed(item);

  if (isPointRedemptionItem) {
    return (
      <div className="flex flex-col items-end">
        <span className="text-base font-medium text-gray-900">{formatCurrency(0, "HKD")}</span>
        <PointRedemptionNoteForCartItem item={item} quantity={quantity} />
        <span className="text-base font-medium text-gray-900">
          {t(msg.cart.total)}: {formatCurrency(0, "HKD")}
        </span>
      </div>
    );
  }

  // Handle add-on items first (priority)
  if (isAddOnItem) {
    return (
      <div className="flex flex-col items-end">
        <span className="text-sm text-gray-400 line-through">{formatCurrency(item.itemPrice, "HKD")}</span>
        <span className="text-base font-medium text-red-600">
          {formatCurrency(item.discountPrice, "HKD")} x {quantity}
        </span>
        <span className="text-xs text-red-600 font-medium">{t(msg.addOnDiscount.discountPrice)}</span>
        <span className="text-base font-medium text-gray-900">
          {t(msg.cart.total)}: {formatCurrency(itemTotal, "HKD")}
        </span>
      </div>
    );
  }

  if (item.chosenPrice === "membershipDiscount") {
    const membershipListPriceEntry = getMembershipListPriceEntry(item.product, item.membership);
    const membershipListPrice = membershipListPriceEntry?.price ?? null;
    const finalPrice = item.membershipDiscountPrice ?? item.finalPrice;
    const showMembershipListPrice =
      membershipListPrice != null && membershipListPrice !== item.itemPrice && membershipListPrice !== finalPrice;

    return (
      <div className="flex flex-col items-end">
        <span className="text-sm text-gray-400 line-through">{formatCurrency(item.itemPrice, "HKD")}</span>
        {showMembershipListPrice && membershipListPriceEntry && (
          <span className="text-sm text-gray-400 font-medium flex items-center gap-1">
            <span className="text-xs text-white bg-green-400 inline-block px-2 py-0.5 rounded-md">
              {t(msg.cart.membershipOffer).replace("{name}", membershipListPriceEntry.membership.name.trim())}
            </span>
            <span className="line-through">{formatCurrency(membershipListPrice, "HKD")}</span>
          </span>
        )}
        <MembershipPriceDisplay membership={item.membership ?? null} amount={finalPrice} quantity={quantity} />
        <ItemDiscount item={item} />
        <span className="text-base font-medium text-gray-900">
          {t(msg.cart.total)}: {formatCurrency(itemTotal, "HKD")}
        </span>
      </div>
    );
  }

  if (item.discountPrice && item.discountPrice === item.finalPrice) {
    return (
      <div className="flex flex-col items-end">
        <span className="text-sm text-gray-400 line-through">{formatCurrency(item.itemPrice, "HKD")}</span>
        <span className="text-base font-medium text-gray-900">
          {formatCurrency(item.discountPrice, "HKD")} x {quantity}
        </span>
        <span className="text-base font-medium text-gray-900">
          {t(msg.cart.total)}: {formatCurrency(itemTotal, "HKD")}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end">
      <span className="text-base font-medium text-gray-900">
        {formatCurrency(item.itemPrice, "HKD")} x {quantity}
      </span>
      <StrikeOriginalTotal item={item} />
      <ItemDiscount item={item} />
      <span className="text-base font-medium text-gray-900">
        {t(msg.cart.total)}: {formatCurrency(itemTotal, "HKD")}
      </span>
    </div>
  );
}

function getMembershipListPriceEntry(product: CartItem["product"], membership: CartItem["membership"]) {
  if (!product.membershipPrices || !membership) return null;

  return product.membershipPrices.find((mp) => mp.price !== null && mp.membership.id === membership.id) ?? null;
}

function StrikeOriginalTotal({ item }: { item: CartItem }) {
  if (!item.itemDiscount || item.itemDiscount === 0) return null;

  return (
    <span className="text-sm text-gray-400 line-through">{formatCurrency((item.itemTotal ?? 0) + item.itemDiscount, "HKD")}</span>
  );
}

function ItemDiscount({ item }: { item: CartItem }) {
  if (!item.scheduleDiscount || item.scheduleDiscount === 0) {
    return null;
  }

  return (
    <div className="mt-2 flex flex-col items-end text-red-600 font-bold">{formatCurrency(item.scheduleDiscount * -1, "HKD")}</div>
  );
}
